import { useState, useEffect } from 'react';
import graphqlService from '../services/graphql';
import { imageCache, extractImageUrls } from '../utils/imageCache';

/**
 * Home 页面数据管理 Hook
 */
export const useHomeData = () => {
  const [categories, setCategories] = useState([]);
  const [recommendedEquipments, setRecommendedEquipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoriesLoading, setCategoriesLoading] = useState(true); // 初始显示骨架屏
  const [equipmentsLoading, setEquipmentsLoading] = useState(true); // 初始显示骨架屏
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('开始加载首页数据...');

        // 第一阶段：优先加载分类数据（快速显示页面结构）
        try {
          const categoriesStartTime = Date.now();
          
          console.log('开始加载分类数据，显示骨架屏...');
          setCategoriesLoading(true);
          
          const categoriesData = await graphqlService.getCategories();
          
          setCategories(categoriesData);
          setCategoriesLoading(false);
          
          console.log(`分类数据加载完成，耗时: ${Date.now() - categoriesStartTime}ms，隐藏骨架屏`);
          
          // 预加载分类图片
          const categoryImageUrls = extractImageUrls(categoriesData);
          if (categoryImageUrls.length > 0) {
            imageCache.preloadBatch(categoryImageUrls, {
              batchSize: 2,
              delay: 50,
              onProgress: (loaded, total) => {
                console.log(`分类图片预加载: ${loaded}/${total}`);
              }
            }).catch(err => console.warn('分类图片预加载失败:', err));
          }
        } catch (err) {
          console.error('分类数据加载失败:', err);
          setCategoriesLoading(false);
        }

        // 第二阶段：加载推荐装备数据
        try {
          const equipmentsStartTime = Date.now();
          
          console.log('开始加载推荐装备数据，显示骨架屏...');
          setEquipmentsLoading(true);
          
          const ids = await graphqlService.getRecommendedEquipments();
          
          // 分批加载装备详情，避免并发过多
          const batchSize = 3;
          const detailList = [];
          
          for (let i = 0; i < ids.length; i += batchSize) {
            const batch = ids.slice(i, i + batchSize);
            
            const batchPromises = batch.map(async (item) => {
              try {
                return await graphqlService.getEquipmentDetailById(item.id);
              } catch (err) {
                console.warn(`装备详情加载失败 ID: ${item.id}`, err);
                return null;
              }
            });

            const batchResults = await Promise.allSettled(batchPromises);
            const batchData = batchResults
              .filter(result => result.status === 'fulfilled' && result.value !== null)
              .map(result => result.value);
            
            detailList.push(...batchData);

            // 实时更新装备列表
            setRecommendedEquipments([...detailList]);

            // 批次间延迟，减少服务器压力
            if (i + batchSize < ids.length) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }

          setEquipmentsLoading(false);
          
          console.log(`推荐装备数据加载完成，耗时: ${Date.now() - equipmentsStartTime}ms，隐藏骨架屏`);

          // 第三阶段：异步预加载装备图片（不阻塞UI）
          setTimeout(() => {
            const equipmentImageUrls = extractImageUrls(detailList);
            if (equipmentImageUrls.length > 0) {
              imageCache.preloadBatch(equipmentImageUrls.slice(0, 10), { // 只预加载前10张
                batchSize: 2,
                delay: 200
              }).catch(err => {
                console.warn('装备图片预加载失败:', err);
              });
            }
          }, 1500); // 延迟1.5秒开始预加载

        } catch (err) {
          console.error('推荐装备数据加载失败:', err);
          setEquipmentsLoading(false);
          setError(err.message);
        }

      } catch (err) {
        console.error('数据加载失败:', err);
        setError(err.message);
        setCategories([]);
        setRecommendedEquipments([]);
        setCategoriesLoading(false);
        setEquipmentsLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return {
    categories,
    recommendedEquipments,
    loading,
    categoriesLoading,
    equipmentsLoading,
    error
  };
};